import { useState } from 'react';
import {
  TrendingUp,
  RefreshCw,
  Lightbulb,
  Target,
  Building2,
  Zap,
  Shield,
  Clock,
  Trash2,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useMarketInsights,
  useInsightsHistory,
  useGenerateInsights,
  useDeleteInsight,
  useInsightsById,
  type InsightsData,
  type InsightsHistoryEntry
} from '@/hooks/use-market-insights';
import { useCurrentUser } from '@/hooks/use-current-user';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';

type TabType = 'internal' | 'external';

function InsightSection({
  icon: Icon,
  title,
  items,
  color
}: {
  icon: React.ElementType;
  title: string;
  items: string[];
  color: string;
}) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border">
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <h3 className="text-sm font-semibold">{title}</h3>
          <Badge variant="outline" className="ml-auto text-[10px]">
            {items.length}
          </Badge>
        </div>
      </div>
      <ul className="divide-y">
        {items.map((item, i) => (
          <li key={i} className="px-4 py-2.5 text-sm text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectorCard({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <ul className="divide-y">
        {items.map((item, i) => (
          <li key={i} className="px-4 py-2.5 text-sm text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightsView({ data }: { data: InsightsData }) {
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
        <p className="text-sm font-medium">{data.summary}</p>
      </div>

      {/* Market Viability */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InsightSection
          icon={TrendingUp}
          title="Market Trends"
          items={data.marketViability.currentTrends}
          color="text-green-600 dark:text-green-400"
        />
        <InsightSection
          icon={Clock}
          title="Contract Patterns"
          items={data.marketViability.contractPatterns}
          color="text-blue-600 dark:text-blue-400"
        />
      </div>

      {/* Competencies */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <InsightSection
          icon={Zap}
          title="Technical"
          items={data.demandedCompetencies.technical}
          color="text-yellow-500"
        />
        <InsightSection
          icon={Building2}
          title="Business"
          items={data.demandedCompetencies.business}
          color="text-purple-500"
        />
        <InsightSection
          icon={Target}
          title="Sector-Specific"
          items={data.demandedCompetencies.sectorSpecific}
          color="text-orange-500"
        />
        <InsightSection
          icon={Lightbulb}
          title="Emerging"
          items={data.demandedCompetencies.emerging}
          color="text-cyan-500"
        />
      </div>

      {/* Sector Insights */}
      <div>
        <h3 className="mb-3 text-sm font-semibold">Sector Insights</h3>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <SectorCard title="Private Sector" items={data.sectorInsights.private} />
          <SectorCard title="Public Sector" items={data.sectorInsights.public} />
          <SectorCard title="Energy Sector" items={data.sectorInsights.energy} />
        </div>
      </div>

      {/* Market Prognosis */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InsightSection
          icon={Clock}
          title="Short-term (6 months)"
          items={data.marketPrognosis.shortTerm}
          color="text-blue-500"
        />
        <InsightSection
          icon={TrendingUp}
          title="Long-term (12-24 months)"
          items={data.marketPrognosis.longTerm}
          color="text-green-500"
        />
      </div>

      {/* Strategic Recommendations */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InsightSection
          icon={Target}
          title="Immediate Actions"
          items={data.strategicRecommendations.immediateActions}
          color="text-red-500"
        />
        <InsightSection
          icon={Lightbulb}
          title="Investment Priorities"
          items={data.strategicRecommendations.investmentPriorities}
          color="text-yellow-500"
        />
        <InsightSection
          icon={Zap}
          title="Competitive Advantages"
          items={data.strategicRecommendations.competitiveAdvantages}
          color="text-green-500"
        />
        <InsightSection
          icon={Shield}
          title="Risk Mitigation"
          items={data.strategicRecommendations.riskMitigation}
          color="text-orange-500"
        />
      </div>

      {/* Sources */}
      {data.sources.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground">
            Sources ({data.sources.length}) ▸
          </summary>
          <ul className="mt-2 space-y-1">
            {data.sources.map((s, i) => (
              <li key={i} className="text-xs text-muted-foreground">
                {s}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function HistoryPanel({
  onSelect,
  selectedId
}: {
  onSelect: (id: string) => void;
  selectedId: string | null;
}) {
  const { data, isLoading } = useInsightsHistory();
  const deleteInsight = useDeleteInsight();
  const entries = data?.data ?? [];

  if (isLoading)
    return <div className="py-4 text-center text-xs text-muted-foreground">Loading history...</div>;
  if (!entries.length)
    return <div className="py-4 text-center text-xs text-muted-foreground">No history yet</div>;

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={`flex items-start gap-3 rounded-md border px-3 py-2 text-sm transition-colors cursor-pointer hover:bg-muted/30 ${
            selectedId === entry.id ? 'border-primary bg-primary/5' : ''
          }`}
          onClick={() => onSelect(entry.id)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] capitalize">
                {entry.type}
              </Badge>
              <span className="text-xs text-muted-foreground">{formatDate(entry.createdAt)}</span>
            </div>
            {entry.summary && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{entry.summary}</p>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              deleteInsight.mutate(entry.id);
            }}
          >
            <Trash2 className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function MarketInsightsPage() {
  const tFn = useT();
  const { canAccessMarketInsights } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<TabType>('internal');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  const { data: latestResp, isLoading } = useMarketInsights(activeTab);
  const generate = useGenerateInsights();

  // Load selected history entry
  const { data: historyResp } = useInsightsById(selectedHistoryId);

  const latest = latestResp?.data ?? null;
  const isGenerating = generate.isPending;
  const displayData = (historyResp?.data ?? latest) as { data?: InsightsData } | null;
  const insights = displayData?.data ?? null;

  if (!canAccessMarketInsights) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-lg font-semibold">Access Restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don&apos;t have permission to view this page. Contact your admin for access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{tFn('nav.marketInsights')}</h1>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setShowHistory((h) => !h)}>
            <Clock className="mr-1.5 h-3.5 w-3.5" />
            {showHistory ? 'Hide History' : 'History'}
          </Button>
          <Button
            size="sm"
            disabled={isGenerating}
            onClick={() => {
              setSelectedHistoryId(null);
              generate.mutate({ type: activeTab });
            }}
          >
            {isGenerating ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <TrendingUp className="mr-1.5 h-3.5 w-3.5" />
                Generate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex rounded-md border border-border">
        {(['internal', 'external'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setSelectedHistoryId(null);
            }}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
              activeTab === tab
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            }`}
          >
            {tab === 'internal' ? 'Internal (Platform Data)' : 'External (Market Research)'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px]">
        {/* Main content */}
        <div>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center rounded-lg border py-20 text-center">
              <RefreshCw className="mb-3 h-8 w-8 animate-spin text-primary/60" />
              <p className="text-sm text-muted-foreground">Loading insights...</p>
            </div>
          ) : isGenerating && !insights ? (
            <div className="flex flex-col items-center justify-center rounded-lg border py-20 text-center">
              <RefreshCw className="mb-3 h-8 w-8 animate-spin text-primary/60" />
              <p className="text-sm text-muted-foreground">
                AI is analyzing {activeTab === 'internal' ? 'your assignments' : 'the market'}...
              </p>
            </div>
          ) : insights ? (
            <InsightsView data={insights} />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20 text-center">
              <TrendingUp className="mb-3 h-10 w-10 text-muted-foreground/50" />
              <h3 className="text-sm font-semibold">No insights generated yet</h3>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Click &quot;Generate&quot; to analyze
                {activeTab === 'internal' ? ' your assignments' : ' the market'} with AI.
              </p>
            </div>
          )}
        </div>

        {/* History sidebar */}
        {showHistory && (
          <div className="rounded-lg border p-3">
            <h3 className="mb-3 text-sm font-semibold">History</h3>
            <HistoryPanel
              onSelect={(id) => setSelectedHistoryId(id)}
              selectedId={selectedHistoryId}
            />
          </div>
        )}
      </div>
    </div>
  );
}
